"use client"

{
  /* 
    if we want this full page width 
    - remove the overflow-hidden
    - calculate based on the page width and not the container width
    */
}
import { Text } from "@/elements"
import { cn } from "@/utils/classnames"
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "motion/react"
import Image from "next/image"
import React, { useEffect, useRef, useState, type JSX } from "react"

import animation from "@/public/marketing/work-types/Animation.jpg"
import apparelDesign from "@/public/marketing/work-types/Apparel Design.jpg"
import brandedContent from "@/public/marketing/work-types/Branded content.jpg"
import campaigns from "@/public/marketing/work-types/Campaigns.jpg"
import commercials from "@/public/marketing/work-types/Commercials.jpg"
import concertCoverage from "@/public/marketing/work-types/Concert Coverage.jpg"
import coverArtwork from "@/public/marketing/work-types/Cover Artwork.jpg"
import documentaries from "@/public/marketing/work-types/Documentaries.jpg"
import editorials from "@/public/marketing/work-types/Editorials.jpg"
import events from "@/public/marketing/work-types/Event.jpg"
import featureFilms from "@/public/marketing/work-types/Feature Films.jpg"
import lookbook from "@/public/marketing/work-types/Lookbook.jpg"
import musicVideos from "@/public/marketing/work-types/Music Videos.jpg"
import packaging from "@/public/marketing/work-types/Packaging.jpg"
import productDesign from "@/public/marketing/work-types/Product Design.jpg"
import propDesigns from "@/public/marketing/work-types/Prop Design.jpg"
import shortFilms from "@/public/marketing/work-types/Short films.jpg"
import socialMedia from "@/public/marketing/work-types/Social Media.jpg"
import stageDesign from "@/public/marketing/work-types/Stage Design.jpg"
import websiteDesign from "@/public/marketing/work-types/Website Design.jpg"

const imagesTop = [
  animation,
  apparelDesign,
  brandedContent,
  campaigns,
  commercials,
  concertCoverage,
  coverArtwork,
  documentaries,
  editorials,
  events,
]

const textSnippetsTop = [
  "Animation",
  "Apparel Design",
  "Branded Content",
  "Campaigns",
  "Commercials",
  "Concert Coverage",
  "Cover Artwork",
  "Documentaries",
  "Editorials",
  "Events",
]
const imagesBottom = [
  featureFilms,
  lookbook,
  musicVideos,
  packaging,
  productDesign,
  propDesigns,
  shortFilms,
  socialMedia,
  stageDesign,
  websiteDesign,
]
const textSnippetsBottom = [
  "Feature Films",
  "Lookbooks",
  "Music Videos",
  "Packaging Design",
  "Product Design",
  "Prop Design",
  "Short Films",
  "Social Media",
  "Stage Design",
  "Website Design",
]

interface VelocityScrollProps {
  default_velocity?: number
  className?: string
}

interface ParallaxProps {
  children: JSX.Element
  baseVelocity: number
  className?: string
  direction?: "left" | "right"
}

export const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

export function CreativeWorkSectionMarquee({ default_velocity = 5, className }: VelocityScrollProps) {
  function ParralaxContent({ children, baseVelocity = 100, className, direction }: ParallaxProps) {
    const baseX = useMotionValue(0)
    const { scrollY } = useScroll()
    const scrollVelocity = useVelocity(scrollY)
    const smoothVelocity = useSpring(scrollVelocity, {
      damping: 100,
      stiffness: 400,
    })

    /* for uni directional change to
      const velocityFactor = useTransform(smoothVelocity, [-1000, 0, 1000], [5, 0, 5], {
    clamp: false,
  })
    */
    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 3.5], {
      clamp: false,
    })

    const [repetitions, setRepetitions] = useState(1)

    const containerRef = useRef<HTMLDivElement>(null)
    const textRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const calculateRepetitions = () => {
        if (containerRef.current && textRef.current) {
          const containerWidth = containerRef.current.offsetWidth
          const textWidth = textRef.current.offsetWidth
          const newRepetitions = Math.ceil((containerWidth * 2) / textWidth) + 1
          setRepetitions(newRepetitions)
        }
      }

      calculateRepetitions()

      const resizeObserver = new ResizeObserver(calculateRepetitions)
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [children])

    /* this makes sure that the marquee content always starts and ends on the same place
    e.g. content = 500px and container = 1000px, then the content will start at -50% and end
    */
    const x = useTransform(
      baseX,
      v => `${wrap(-(((textRef.current?.offsetWidth ?? 0) / (containerRef.current?.offsetWidth ?? 0)) * 100), 0, v)}%`
    )

    /* for unidirectional scrolling use this */
    /* const directionFactor = direction === "left" ? -1 : 1
    useAnimationFrame((t, delta) => {
      let moveBy = directionFactor * baseVelocity * (delta / 1000)

      const speedIncrease = 1 + velocityFactor.get()
      moveBy *= speedIncrease
      baseX.set(baseX.get() + moveBy)
    }) */

    const directionFactor = React.useRef<number>(1)
    useAnimationFrame((t, delta) => {
      let moveBy = directionFactor.current * baseVelocity * (delta / 1000)

      if (velocityFactor.get() < 0) {
        directionFactor.current = -1
      } else if (velocityFactor.get() > 0) {
        directionFactor.current = 1
      }

      moveBy += directionFactor.current * moveBy * velocityFactor.get()

      baseX.set(baseX.get() + moveBy)
    })

    return (
      <div className="w-full overflow-x-visible whitespace-nowrap" ref={containerRef}>
        <motion.div className={cn("flex flex-row", className)} style={{ x }}>
          {Array.from({ length: repetitions }).map((_, i) => (
            <div key={i} ref={i === 0 ? textRef : null} className="shrink-0">
              {children}
            </div>
          ))}
        </motion.div>
      </div>
    )
  }

  return (
    <section className="relative flex w-screen flex-col gap-y-4 md:gap-y-8">
      <ParralaxContent baseVelocity={default_velocity} className={className}>
        <div className="flex shrink-0 flex-row items-center">
          {imagesTop.map((image, i) => (
            <div key={`top-group-${i}`} className="flex items-center">
              <Image
                src={image}
                width={400}
                height={400}
                alt={textSnippetsTop[i % textSnippetsTop.length]}
                className="size-14 md:size-20"
                loading="lazy"
              />
              <Text
                variant="h4"
                className="h-min content-center px-4 text-marketing-primary-light md:px-8 dark:text-marketing-primary-dark">
                {textSnippetsTop[i % textSnippetsTop.length]}
              </Text>
            </div>
          ))}
        </div>
      </ParralaxContent>
      <ParralaxContent baseVelocity={-default_velocity} className={className}>
        <div className="flex shrink-0 flex-row items-center">
          {imagesBottom.map((image, i) => (
            <div key={`bottom-group-${i}`} className="flex items-center">
              <Image
                src={image}
                alt={textSnippetsBottom[i % textSnippetsBottom.length]}
                width={400}
                height={400}
                className="size-14 md:size-20"
                loading="lazy"
              />
              <Text
                variant="h4"
                className="h-min content-center px-4 text-marketing-primary-light md:px-8 dark:text-marketing-primary-dark">
                {textSnippetsBottom[i % textSnippetsBottom.length]}
              </Text>
            </div>
          ))}
        </div>
      </ParralaxContent>

      {/* <div className="absolute left-0 top-0 h-full w-[300px] bg-gradient-to-r from-marketing-background-1-light to-transparent dark:from-marketing-background-1-dark" />
      <div className="absolute right-0 top-0 h-full w-[300px] bg-gradient-to-l from-marketing-background-1-light to-transparent dark:from-marketing-background-1-dark" /> */}
    </section>
  )
}
